import React from 'react';
import { LogoMark } from './Logo';

/**
 * VERUM Q — error boundary
 *
 * Doctrine compliance:
 *   - lucide RefreshCw icon removed.
 *   - Pill buttons swapped for square button-mirror.
 *   - font-heading + font-body legacy classes replaced with the system
 *     defaults (mono primary, editorial serif for the explanatory line).
 *   - The development-only error excerpt renders in a hairline-bordered
 *     mono block instead of a rounded card.
 *
 * Note: this is a class component because React's error boundaries
 * require it. The doctrine has nothing against class components — only
 * against the visual idiom of the legacy code.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Keep the console signal — useful for Posthog session replay debugging.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, errorInfo);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
          <div className="max-w-md w-full">
            <div className="flex justify-center mb-8">
              <LogoMark size={32} />
            </div>

            <p className="label-gap text-center">Error</p>
            <h1 className="mt-3 text-2xl tracking-tight text-foreground text-center">
              Something went wrong
            </h1>
            <p className="mt-4 text-sm text-muted-foreground text-center font-editorial leading-relaxed">
              Your entries are safe. The page encountered an unexpected error;
              nothing has been lost.
            </p>

            <div className="flex flex-wrap justify-center gap-3 mt-8">
              <button
                type="button"
                onClick={this.handleReload}
                className="button-mirror"
                data-testid="error-reload-btn"
              >
                Reload page
              </button>
              <button
                type="button"
                onClick={this.handleGoHome}
                className="button-mirror button-mirror--ghost"
                data-testid="error-go-home-btn"
              >
                Go to dashboard
              </button>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <pre className="mt-8 text-left text-xs text-muted-foreground border border-border p-3 overflow-x-auto font-mono leading-relaxed">
                {this.state.error.toString()}
              </pre>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
